// Components
import EntitySearch, {
  EmptyQuerySearchOptions
} from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';

// types
import { PlanningEntityIdentifier, PlanningEntityType } from 'generated/index';

// hooks
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityListener } from 'components/mrp/mrp.hooks';

// stores/context
import { useCalendarInteractionStore } from '../../../store/calendarInteractionStore.store';
import { useShallow } from 'zustand/shallow';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useCalendarNavigationStore } from '../../../store/calendarNavigationStore.store';
import { ShortcutContext } from 'components/shared/ShortcutProvider/ShortcutContext';

// utils
import { getPlanningEntityIdentifiersFromEvent } from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { addEntityToEvent } from './EntityQuickConnect.utils';

// dialog
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { EntityType } from 'components/mrp/mrp.consts';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';

const EntityQuickConnect = () => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const {
    showEntityQuickConnect,
    eventRef,
    setShowEntityQuickConnect,
    setIsProcessing
  } = useCalendarInteractionStore(
    useShallow((state) => ({
      showEntityQuickConnect: state.showEntityQuickConnect,
      eventRef: state.eventRef,
      isProcessing: state.isProcessing,
      setShowEntityQuickConnect: state.control.setShowEntityQuickConnect,
      setIsProcessing: state.control.setIsProcessing
    }))
  );

  const { setActiveShortcutContexts, shortcuts } = useContext(ShortcutContext);

  useEffect(() => {
    setActiveShortcutContexts?.(
      !!showEntityQuickConnect ? [] : Object.keys(shortcuts)
    );
  }, [showEntityQuickConnect]);

  const event = useCalendarNavigationStore((state) => state.event);

  const calendarsObj = useCalendarStore((state) => state.calendars);
  const calendars = useMemo(() => Object.values(calendarsObj), [calendarsObj]);

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: []
  });

  const emptyQueryOptions: EmptyQuerySearchOptions = useMemo(() => {
    return {
      groupLabel: t('openedCalendars'),
      options: calendars
        .flatMap(({ sources }) => sources)
        .map((calendarData) => ({
          id: calendarData.planningEntityIdentifier.entityId!,
          label: calendarData.label,
          group: t(`openedCalendars`),
          isLocal: true,
          entity: calendarData.planningEntityIdentifier
        }))
    };
  }, [calendars]);

  const activeEntities = useMemo(() => {
    if (!event?.planningEvent) return [];

    return getPlanningEntityIdentifiersFromEvent(event.planningEvent).map(
      ({ entityId }) => entityId!
    );
  }, [event]);

  const entityFilters: PlanningEntityType[] = useMemo(() => {
    const defaultEntityTypes = Object.values(EntityType);

    if (!event || !event.planningEvent || event.planningEvent.workPlace == null)
      return defaultEntityTypes;

    return defaultEntityTypes.filter((type) => type !== 'WORKPLACE');
  }, [event]);

  const { openDialog, onClose } = useContext(DialogContext);

  const [fetchEntities, setFetchEntities] = useState<
    PlanningEntityIdentifier[]
  >([]);

  const onSelectEntity = async (entity: PlanningEntityIdentifier) => {
    if (!event) return;

    setShowEntityQuickConnect(false);
    setIsProcessing(true);

    const formData = addEntityToEvent({
      event: event.planningEvent,
      entity
    });

    setFetchEntities([
      ...getPlanningEntityIdentifiersFromEvent(event.planningEvent),
      entity
    ]);

    openDialog?.(
      'smart-action-dialog-quickconnect-update',
      DialogType.SMART_ACTION_DIALOG,
      {
        actionType: 'update',
        appointmentExceptions: formData?.appointmentExceptions,
        payload: formData
      }
    );
  };

  useEffect(() => {
    onClose?.(
      'smart-action-dialog-quickconnect-update',
      DialogType.SMART_ACTION_DIALOG,
      async (props) => {
        try {
          if (props?.ok) {
            await refetchByPlanningEntityIdentifiers(fetchEntities);
          }
        } catch (error) {
          console.error('Failed to refetch planning entities', error);
        } finally {
          setIsProcessing(false);
        }
      }
    );
  }, [
    fetchEntities,
    onClose,
    refetchByPlanningEntityIdentifiers,
    setIsProcessing
  ]);

  return (
    <>
      <Popover
        id={`entity-quick-connect`}
        forceComponentWidth
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom'
        }}
        useTransition={false}
        useForcedState={true}
        forceOpen={showEntityQuickConnect}
        onClose={() => setShowEntityQuickConnect(false)}
        childRef={eventRef}
        component={
          <EntitySearch
            activeEntities={activeEntities}
            entityTypeFilters={entityFilters}
            emptyQuerySearchOptions={emptyQueryOptions}
            minWidth={250}
            mode="compact"
            onChange={onSelectEntity}
            onEscape={() => setShowEntityQuickConnect(false)}
          />
        }
      />
    </>
  );
};

export default EntityQuickConnect;
